import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { formatDistance } from '../../lib/nearest';

const RADII = [100, 250, 500, 1000, 2500, 5000, 10000, 50000];

/** Floating results list for the Find nearest tool: radius control + (type, name/id, distance) rows. */
// `embedded`: rendered inside a dock / floating window, which owns position and size.
export default function NearestPanel({ nearest, mapRefHolder, embedded = false }) {
  const { C } = useTheme();
  const flyTo = (lonLat) => {
    const map = mapRefHolder?.current?.getMap?.();
    if (map) map.easeTo({ center: lonLat, duration: 400 });
  };
  const selectStyle = { fontSize: 11, padding: '3px 6px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0 };

  return (
    <div data-testid="nearest-panel" style={{
      position: 'absolute', top: 46, left: 10, zIndex: 5, width: 270, maxHeight: '60%', overflow: 'auto',
      background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.35)', fontSize: 12, color: C.text1,
      ...(embedded ? { position: 'static', width: 'auto', maxHeight: 'none', border: 'none', borderRadius: 0, boxShadow: 'none', minHeight: '100%' } : {}),
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderBottom: `1px solid ${C.border}` }}>
        <strong style={{ flex: 1, color: C.text0 }}>Find nearest</strong>
        <label style={{ fontSize: 10, color: C.text2 }}>Radius{' '}
          <select aria-label="Search radius" value={nearest.radiusM} onChange={(e) => nearest.setRadiusM(Number(e.target.value))} style={selectStyle}>
            {RADII.map((r) => <option key={r} value={r}>{formatDistance(r)}</option>)}
          </select>
        </label>
        {nearest.point && (
          <button onClick={nearest.clear} title="Clear results" style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 14 }}>×</button>
        )}
      </div>
      <div style={{ padding: '6px 10px' }}>
        {!nearest.point && <div style={{ color: C.text3 }}>Click the map to list the closest features.</div>}
        {nearest.point && nearest.busy && <div style={{ color: C.text3 }}>Searching…</div>}
        {nearest.error && <div style={{ color: '#fbbf24', marginBottom: 4 }}>{nearest.error}</div>}
        {nearest.point && !nearest.busy && nearest.rows.length === 0 && (
          <div style={{ color: C.text3 }}>Nothing within {formatDistance(nearest.radiusM)}.</div>
        )}
        {nearest.rows.map((r, i) => (
          <button key={`${r.source}-${r.layerId || ''}-${r.id}-${i}`} onClick={() => flyTo(r.lonLat)} title={r.layerName ? `Layer: ${r.layerName}` : 'Saved feature'}
            style={{ display: 'flex', width: '100%', alignItems: 'baseline', gap: 6, padding: '4px 0', background: 'none', border: 'none', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', textAlign: 'left', color: C.text1 }}>
            <span style={{ width: 14, color: C.text3 }}>{i + 1}</span>
            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 99, background: r.source === 'db' ? 'rgba(52,211,153,0.15)' : 'rgba(251,191,36,0.15)', color: r.source === 'db' ? '#34d399' : '#fbbf24' }}>{r.type}</span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: C.text0 }}>{r.name}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatDistance(r.distanceM)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
